/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '../../main/context/SiteSettingsContext';
import { isRtlLocale } from '../../../i18n/language';
import HtmlDocumentViewer from './HtmlDocumentViewer';
import { DocumentCompanyHeader, DocumentCompanyFooter } from './DocumentCompanyBranding';
import { OmrUnitSymbol } from './OmrUnitSymbol';
import {
  customerName,
  formatDocDateTime,
  getDocLocale,
  normalizeAppliedRules,
  shipmentBilledTotal,
  shipmentTotal,
} from './htmlDocumentUtils';
import {
  DEFAULT_KG_UNIT_LABELS,
  invoicePdfBilledQtyUnits,
  invoiceQtyUnit,
  formatInvoiceWeightNumber,
  hasInvoiceWeightKg,
  invoicePdfShowsWeightColumn,
} from '../../main/utils/formatInvoiceQty';
import { formatPaymentStatus } from '../../main/widgets/PaymentStatusOptionList';
import {
  buildInvoicePaymentSummary,
  formatPaymentTransactionDate,
  parsePaymentAmounts,
} from '../utils/invoicePaymentTransactions';

function AmountCell({ value, className, symbolWidth = 9, symbolHeight = 6 }) {
  return (
    <span className={`doc-amount-inline ${className || ''}`.trim()}>
      {Number(value).toFixed(3)}
      <OmrUnitSymbol width={symbolWidth} height={symbolHeight} />
    </span>
  );
}

function PaymentSummaryBox({ summary, invoiceTotal, t, isRtl }) {
  const {
    totalNet,
    totalRefunded,
    balanceDue,
    overpaid,
    isPaidInFull,
    hasPayments,
    hasRefunds,
  } = summary;

  let totalRowClass = 'doc-summary-row doc-summary-row-total';
  let totalLabel = t('pdfDocuments.invoice.balanceDue');
  let totalValue = balanceDue;

  if (overpaid > 0) {
    totalRowClass += ' doc-summary-row-overpaid';
    totalLabel = t('pdfDocuments.invoice.overpaidCredit');
    totalValue = overpaid;
  } else if (isPaidInFull || (balanceDue === 0 && invoiceTotal > 0 && hasPayments)) {
    totalRowClass += ' doc-summary-row-paid';
    totalLabel = t('pdfDocuments.invoice.paidInFull');
    totalValue = 0;
  }

  return (
    <div className="doc-summary-box" style={isRtl ? { marginRight: 'auto', marginLeft: 0 } : undefined}>
      <div className="doc-summary-row">
        <span className="doc-summary-label">{t('pdfDocuments.invoice.invoiceTotal')}</span>
        <span className="doc-summary-value">
          {invoiceTotal.toFixed(3)}
          <OmrUnitSymbol width={9} height={6} />
        </span>
      </div>
      {hasPayments ? (
        <div className="doc-summary-row">
          <span className="doc-summary-label">{t('pdfDocuments.invoice.totalPaidNet')}</span>
          <span className="doc-summary-value">
            {totalNet.toFixed(3)}
            <OmrUnitSymbol width={9} height={6} />
          </span>
        </div>
      ) : null}
      {hasRefunds ? (
        <div className="doc-summary-row">
          <span className="doc-summary-label">{t('pdfDocuments.invoice.totalRefunded')}</span>
          <span className="doc-summary-value doc-amount-danger">
            {totalRefunded.toFixed(3)}
            <OmrUnitSymbol width={9} height={6} />
          </span>
        </div>
      ) : null}
      <div className={totalRowClass}>
        <span className="doc-summary-label">{totalLabel}</span>
        <span className="doc-summary-value">
          {totalValue.toFixed(3)}
          <OmrUnitSymbol width={10} height={6} />
        </span>
      </div>
    </div>
  );
}

/**
 * Group non-excluded applied rules by settlement_group_code_snapshot for PDF sections.
 * Excluded rules stay listed but are omitted from group subtotals.
 */
function groupAppliedRulesForPdf(rules) {
  const groups = [];
  const indexByKey = new Map();
  const list = Array.isArray(rules) ? rules : [];
  if (list.length === 0) {
    return [{ key: '_empty', header: null, separate: false, rules: [null] }];
  }
  list.forEach((rule) => {
    const key = rule?.settlement_group_code_snapshot || '_shared';
    let group = indexByKey.get(key);
    if (!group) {
      group = {
        key,
        header: null,
        separate: Boolean(rule?.separate_checkout_snapshot),
        rules: [],
      };
      indexByKey.set(key, group);
      groups.push(group);
    }
    if (rule?.separate_checkout_snapshot) group.separate = true;
    group.rules.push(rule);
  });
  return groups;
}

function settlementGroupHeader(group, isArabic, t) {
  if (!group || group.key === '_empty') return null;
  const first = (group.rules || []).find(Boolean);
  if (!first) {
    return t('pdfDocuments.invoice.sharedChargesGroup');
  }
  if (isArabic) {
    return (
      first.settlement_group_name_ar_snapshot
      || first.settlement_group_name_snapshot
      || first.settlement_group_code_snapshot
      || t('pdfDocuments.invoice.sharedChargesGroup')
    );
  }
  return (
    first.settlement_group_name_snapshot
    || first.settlement_group_code_snapshot
    || t('pdfDocuments.invoice.sharedChargesGroup')
  );
}

/**
 * Billed quantity for one PDF row.
 * The column title is the unit, so the cell is the number unless units differ.
 */
function billedQtyCellText(shipment, lang, emDash, repeatUnit) {
  // Quantity stored on the invoice shipment line.
  const qty = shipment?.invoice_quantity;
  // Blank quantity prints the em dash.
  if (qty == null || qty === '') return emDash;
  // One shared unit: the column title already names it.
  if (!repeatUnit) return String(qty);
  // Mixed units: keep this row's unit beside the number.
  const unit = invoiceQtyUnit(shipment, lang);
  // Number plus unit, or the bare number when this row has no label.
  return unit ? `${qty} ${unit}` : String(qty);
}

function InvoicePdfWeightCell({ shipment, lang, emDash }) {
  if (!hasInvoiceWeightKg(shipment)) {
    return emDash;
  }

  const value = formatInvoiceWeightNumber(shipment.weight, lang);
  const unit =
    lang === 'ar'
      ? DEFAULT_KG_UNIT_LABELS.invoice_unit_label_ar
      : DEFAULT_KG_UNIT_LABELS.invoice_unit_label_en;

  return (
    <span className="doc-weight-cell">
      <span className="doc-weight-value">{value}</span>
      <span className="doc-weight-unit">{unit}</span>
    </span>
  );
}

function ShipmentPricingRows({
  shipment,
  emDash,
  t,
  isRtl,
  isArabic,
  showWeightColumn,
  repeatBilledUnit,
}) {
  const rules = normalizeAppliedRules(shipment?.applied_rules);
  const shipmentSum = shipmentBilledTotal(shipment);
  const groups = groupAppliedRulesForPdf(rules);

  // Rowspan: identity cells span all rule rows + group headers + group subtotals + shipment total
  let bodyRowCount = 0;
  groups.forEach((g) => {
    const hasHeader = g.key !== '_empty' && g.rules.some(Boolean);
    if (hasHeader) bodyRowCount += 1; // group header
    bodyRowCount += Math.max(g.rules.length, 1);
    if (hasHeader) bodyRowCount += 1; // group subtotal
  });
  const metadataRowSpan = bodyRowCount + 1; // + shipment total

  let rowCursor = 0;
  const rowsOut = [];

  groups.forEach((group) => {
    const groupHeader = settlementGroupHeader(group, isArabic, t);
    const hasHeader = Boolean(groupHeader) && group.key !== '_empty';
    const displayRules = group.rules.length > 0 ? group.rules : [null];
    const groupSubtotal = displayRules.reduce((sum, rule) => {
      if (!rule || rule.excluded) return sum;
      const n = Number(rule.amount);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);

    if (hasHeader) {
      const isFirst = rowCursor === 0;
      rowsOut.push(
        <tr
          key={`${shipment?.id}-sgh-${group.key}`}
          className={isFirst ? 'doc-shipment-first-row' : ''}
        >
          {isFirst ? (
            <>
              <td rowSpan={metadataRowSpan} className="doc-shipment-identity">
                {shipment?.id ?? emDash}
              </td>
              <td rowSpan={metadataRowSpan}>{shipment?.tracking_number || emDash}</td>
              <td rowSpan={metadataRowSpan}>{shipment?.shipper_reference || emDash}</td>
              <td rowSpan={metadataRowSpan} className="doc-cell-center">
                {shipment?.packages_quantity ?? emDash}
              </td>
              <td rowSpan={metadataRowSpan} className="doc-cell-number">
                {/* Quantity number; the column title is the unit name. */}
                {billedQtyCellText(shipment, isArabic ? 'ar' : 'en', emDash, repeatBilledUnit)}
              </td>
              {showWeightColumn ? (
                <td rowSpan={metadataRowSpan} className="doc-cell-number doc-col-weight">
                  <InvoicePdfWeightCell
                    shipment={shipment}
                    lang={isArabic ? 'ar' : 'en'}
                    emDash={emDash}
                  />
                </td>
              ) : null}
            </>
          ) : null}
          <td colSpan={2} className="doc-settlement-group-header">
            <strong>{groupHeader}</strong>
            {group.separate ? (
              <span className="doc-rule-excluded-tag">
                {' '}({t('pdfDocuments.invoice.paidSeparately')})
              </span>
            ) : null}
          </td>
        </tr>
      );
      rowCursor += 1;
    }

    displayRules.forEach((rule, index) => {
      const isFirst = rowCursor === 0;
      const excluded = Boolean(rule?.excluded);
      const amount = Number(rule?.amount);
      const ruleName = isArabic
        ? rule?.label_ar || rule?.rule_name_ar || rule?.rule_name || rule?.label
        : rule?.label || rule?.rule_name || rule?.rule_name_ar || rule?.label_ar;

      rowsOut.push(
        <tr
          key={String(rule?.id ?? `${shipment?.id}-empty-${group.key}-${index}`)}
          className={[
            isFirst ? 'doc-shipment-first-row' : '',
            excluded ? 'doc-rule-excluded' : '',
          ].filter(Boolean).join(' ')}
        >
          {isFirst ? (
            <>
              <td rowSpan={metadataRowSpan} className="doc-shipment-identity">
                {shipment?.id ?? emDash}
              </td>
              <td rowSpan={metadataRowSpan}>{shipment?.tracking_number || emDash}</td>
              <td rowSpan={metadataRowSpan}>{shipment?.shipper_reference || emDash}</td>
              <td rowSpan={metadataRowSpan} className="doc-cell-center">
                {shipment?.packages_quantity ?? emDash}
              </td>
              <td rowSpan={metadataRowSpan} className="doc-cell-number">
                {/* Quantity number; the column title is the unit name. */}
                {billedQtyCellText(shipment, isArabic ? 'ar' : 'en', emDash, repeatBilledUnit)}
              </td>
              {showWeightColumn ? (
                <td rowSpan={metadataRowSpan} className="doc-cell-number doc-col-weight">
                  <InvoicePdfWeightCell
                    shipment={shipment}
                    lang={isArabic ? 'ar' : 'en'}
                    emDash={emDash}
                  />
                </td>
              ) : null}
            </>
          ) : null}
          <td>{rule ? ruleName || emDash : emDash}</td>
          <td className="doc-cell-amount">
            {rule && Number.isFinite(amount) ? <AmountCell value={amount} /> : emDash}
            {excluded ? (
              <span className="doc-rule-excluded-tag">
                ({t('pdfDocuments.invoice.ruleExcluded')})
              </span>
            ) : null}
          </td>
        </tr>
      );
      rowCursor += 1;
    });

    if (hasHeader) {
      rowsOut.push(
        <tr key={`${shipment?.id}-sgst-${group.key}`} className="doc-settlement-group-subtotal">
          <td style={{ textAlign: isRtl ? 'left' : 'right' }}>
            {t('pdfDocuments.invoice.groupSubtotal')}
          </td>
          <td className="doc-cell-amount">
            <AmountCell value={groupSubtotal} />
          </td>
        </tr>
      );
      rowCursor += 1;
    }
  });

  return (
    <>
      {rowsOut}
      <tr className="doc-shipment-subtotal-row">
        <td style={{ textAlign: isRtl ? 'left' : 'right' }}>
          {t('pdfDocuments.invoice.shipmentTotal')}
        </td>
        <td className="doc-cell-amount">
          <AmountCell value={shipmentSum} />
        </td>
      </tr>
    </>
  );
}

function ShipmentsPricingTable({ shipments, emDash, t, isRtl, isArabic, invoiceTotal }) {
  const showWeightColumn = invoicePdfShowsWeightColumn(shipments);
  // Arabic uses the Arabic unit label; other locales use the English label.
  const qtyLang = isArabic ? 'ar' : 'en';
  // Distinct units on this invoice (usually one: Kg or CBM).
  const billedQtyUnits = invoicePdfBilledQtyUnits(shipments, qtyLang);
  // Column title is that unit name, or the generic label when none is set.
  const billedQtyHeader = billedQtyUnits.length
    ? billedQtyUnits.join(' / ')
    : t('pdfDocuments.invoice.colInvoiceQuantity');
  // Mixed units need the unit repeated on each row.
  const repeatBilledUnit = billedQtyUnits.length > 1;
  const tableClassName = [
    'doc-table',
    'doc-shipments-pricing-table',
    showWeightColumn ? 'doc-shipments-pricing-table--with-weight' : '',
  ].filter(Boolean).join(' ');
  const totalLabelColSpan = showWeightColumn ? 7 : 6;

  return (
    <table className={tableClassName}>
      <thead>
        <tr>
          <th>{t('pdfDocuments.invoice.colShipmentId')}</th>
          <th>{t('pdfDocuments.invoice.colTrackingNumber')}</th>
          <th>{t('pdfDocuments.invoice.colShipperReference')}</th>
          <th>{t('pdfDocuments.invoice.colQty')}</th>
          <th>{billedQtyHeader}</th>
          {showWeightColumn ? (
            <th className="doc-col-weight">{t('pdfDocuments.invoice.colWeight')}</th>
          ) : null}
          <th>{t('pdfDocuments.invoice.colRuleLabel')}</th>
          <th>{t('pdfDocuments.invoice.colRuleAmount')}</th>
        </tr>
      </thead>
      <tbody>
        {shipments.map((shipment) => (
          <ShipmentPricingRows
            key={String(shipment.id)}
            shipment={shipment}
            emDash={emDash}
            t={t}
            isRtl={isRtl}
            isArabic={isArabic}
            showWeightColumn={showWeightColumn}
            repeatBilledUnit={repeatBilledUnit}
          />
        ))}
      </tbody>
      <tfoot>
        <tr className="doc-invoice-total-row">
          <td colSpan={totalLabelColSpan} style={{ textAlign: isRtl ? 'left' : 'right' }}>
            {t('pdfDocuments.invoice.totalShipments')}
          </td>
          <td className="doc-cell-amount">
            <AmountCell value={invoiceTotal} symbolWidth={10} />
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

const ShipmentInvoice = ({ invoiceData }) => {
  const { t, i18n } = useTranslation('ui');
  const { settings } = useSiteSettings();
  const isRtl = isRtlLocale(i18n.language);
  const emDash = t('pdfDocuments.common.emDash');
  const dateLocale = getDocLocale(i18n.language);
  const isArabic = dateLocale === 'ar';
  const shipments = invoiceData?.shipments || [];
  const invoiceTotal = shipmentTotal(shipments);
  const customer = invoiceData?.customer;
  const customerDisplayName = customerName(customer) || emDash;

  const paymentSummary = useMemo(
    () => buildInvoicePaymentSummary(invoiceTotal, invoiceData?.payments),
    [invoiceTotal, invoiceData?.payments],
  );

  const details = [
    { label: t('pdfDocuments.invoice.invoiceFor'), value: customerDisplayName },
    { label: t('pdfDocuments.common.phone'), value: customer?.phone_number || emDash },
    { label: t('pdfDocuments.invoice.invoiceDate'), value: formatDocDateTime(invoiceData?.created_at, dateLocale, emDash) },
    invoiceData?.payment_status
      ? {
          label: t('pdfDocuments.invoice.paymentStatus'),
          value: formatPaymentStatus(invoiceData.payment_status),
        }
      : null,
  ].filter(Boolean);

  return (
    <HtmlDocumentViewer title={t('pdfDocuments.invoice.title')} isRtl={isRtl} compact>
      <div className="doc-invoice">
        <div className="doc-top-row">
          <div className="doc-title">{t('pdfDocuments.invoice.title')}</div>
          <div className="doc-id-badge">#{invoiceData?.id}</div>
        </div>

        <DocumentCompanyHeader settings={settings} />
        <div className="doc-divider" />

        <div className="doc-invoice-meta-row">
          <div className="doc-invoice-meta-main">
            <h3 className="doc-section-title">{t('pdfDocuments.invoice.invoiceDetails')}</h3>
            <div className="doc-invoice-details-grid">
              {details.map((row) => (
                <div key={row.label} className="doc-invoice-detail-item">
                  <span className="doc-invoice-detail-label">{row.label}</span>
                  <span className="doc-invoice-detail-value">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
          <PaymentSummaryBox
            summary={paymentSummary}
            invoiceTotal={invoiceTotal}
            t={t}
            isRtl={isRtl}
          />
        </div>

        <h3 className="doc-section-title">{t('pdfDocuments.common.shipments')}</h3>
        {shipments.length === 0 ? (
          <p className="doc-muted">{t('pdfDocuments.invoice.emptyShipments')}</p>
        ) : (
          <ShipmentsPricingTable
            shipments={shipments}
            emDash={emDash}
            t={t}
            isRtl={isRtl}
            isArabic={isArabic}
            invoiceTotal={invoiceTotal}
          />
        )}

        <h3 className="doc-section-title">{t('pdfDocuments.invoice.paymentTransactions')}</h3>
        {paymentSummary.ledger.length === 0 ? (
          <p className="doc-muted">{t('pdfDocuments.invoice.emptyPayments')}</p>
        ) : (
          <table className="doc-table">
            <thead>
              <tr>
                <th>{t('pdfDocuments.invoice.colTxDate')}</th>
                <th>{t('pdfDocuments.invoice.colTxReference')}</th>
                <th>{t('pdfDocuments.invoice.colTxMethod')}</th>
                <th>{t('pdfDocuments.invoice.colTxAmount')}</th>
              </tr>
            </thead>
            <tbody>
              {paymentSummary.ledger.map((payment) => {
                const { refunded, net, isFullRefund } = parsePaymentAmounts(payment);
                const isRefundedTx = refunded > 0;
                const amountClass = isFullRefund
                  ? 'doc-amount-muted'
                  : isRefundedTx
                    ? 'doc-amount-danger'
                    : '';

                return (
                  <tr key={String(payment.id)}>
                    <td>{formatPaymentTransactionDate(payment, dateLocale, emDash)}</td>
                    <td className="doc-tx-ref" title={payment.transaction_reference || `#${payment.id}`}>
                      <span className="doc-tx-ref-text">
                        {payment.transaction_reference || `#${payment.id}`}
                      </span>
                      {payment.refund_reason ? (
                        <div className="doc-tx-refund-reason">{payment.refund_reason}</div>
                      ) : null}
                    </td>
                    <td>{payment.payment_method || emDash}</td>
                    <td>
                      <AmountCell value={net} className={amountClass} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {invoiceData?.note ? (
          <div className="doc-notes-box">
            <div className="doc-notes-title">{t('pdfDocuments.common.notes')}</div>
            <div className="doc-notes-body">{invoiceData.note}</div>
          </div>
        ) : null}

        <DocumentCompanyFooter settings={settings} />
        <p className="doc-footer-thanks">{t('pdfDocuments.invoice.footerThanks')}</p>
      </div>
    </HtmlDocumentViewer>
  );
};

export default ShipmentInvoice;
